import type { NextPageContext } from 'next'
import { inject, observer } from 'mobx-react'

import { fetchMenu } from 'utils/api/loadMenu/loadMenu'
import type { LanguageCode } from 'utils/utils'
import { getLocaleStr, codeToLocale, getTranslatedTextByKey, safeStringify } from 'utils/utils'
import { CACHE_POLICY } from '../../../utils/constants'
import CustomHead from '../../../components/CustomHead'
import Legal from 'static/pages/Legal'
import type _Infra from 'mobx/Infra'

interface LegalChainProps {
	Infra: typeof _Infra
	data: any
	lang: LanguageCode
}

const LegalChain = inject('Infra')(
	observer(({ Infra, data, lang }: LegalChainProps) => {
		const language = codeToLocale[lang] || 'en_US'
		const title = getLocaleStr(Infra.appParams?.pageText?.menu?.title, codeToLocale[lang] || 'en_US')

		// Remove 'ref' from appParams because it throws an error on CustomHead (string ref prop not allowed on function component, see https://legacy.reactjs.org/docs/refs-and-the-dom.html#legacy-api-string-refs)
		const { ref, ...appParamsWithoutRef } = Infra.appParams

		return (
			<>
				<CustomHead {...appParamsWithoutRef} title={title} description={title} lang={lang} key={1} />
				<Legal
					title={getTranslatedTextByKey(Infra.appParams.features.menuGridHeader?.title.key, 'Menu Grid Header')}
					content={getLocaleStr(data?.desc, language)}
				/>
			</>
		)
	})
)

export async function getServerSideProps(context: NextPageContext & { req: { extra: { appParams: any; nextJsError: string } } }) {
	// Static menu page data fetching
	if (context.req && !context.req.extra?.nextJsError) {
		try {
			const lang = context.query.lang as string
			const appParams = context.req.extra?.appParams
			const data = await fetchMenu(appParams.c, appParams.c)

			if (!data) {
				throw new Error('Failed to fetch menu data for /legal page', appParams.c)
			}

			if (context.res) {
				context.res.setHeader(
					'Cache-Control',
					`public, s-maxage=${CACHE_POLICY.DEFAULT.MAX_AGE}, stale-while-revalidate=${CACHE_POLICY.DEFAULT.REVALIDATED_AGE}`
				)
			}

			return {
				props: {
					data,
					lang,
				},
			}
		} catch (e: any) {
			console.error(safeStringify(e))
		}
	}

	if (context.res) {
		context.res.statusCode = 500
		context.res.setHeader('Cache-Control', 'no-store')
	}

	return { props: { data: null } }
}

export default LegalChain
